<!-- src/views/CoreInformation.vue -->

<template>
  <div class="container core-information-view coredeck-page">
    <div class="section">
      <div class="card">
        <div class="card-content">
          <div class="core-header">
            <div>
              <span class="card-title"> Core Information </span>
            </div>

            <div class="core-actions">
              <button
                class="btn blue-grey darken-2"
                type="button"
                :disabled="loading || !activeVenueId"
                @click="loadCoreInformation"
              >
                Refresh
              </button>

              <button
                class="btn green darken-2"
                type="button"
                :disabled="saving || loading || !activeVenueId"
                @click="saveCoreInformation"
              >
                {{ saving ? "Saving..." : "Save Core Information" }}
              </button>
            </div>
          </div>

          <div
            v-if="!activeVenueId"
            class="card-panel red lighten-5 red-text text-darken-3"
          >
            No venue selected. Select a venue from the VenueSnapshot dropdown first.
          </div>

          <div v-if="error" class="card-panel red lighten-5 red-text text-darken-3">
            {{ error }}
          </div>

          <div v-if="success" class="card-panel green lighten-5 green-text text-darken-3">
            {{ success }}
          </div>

          <div v-if="loading" class="progress">
            <div class="indeterminate"></div>
          </div>

          <template v-for="section in visibleSections" :key="section.title">
            <div class="section-block">
              <div class="section-heading-row">
                <h5>
                  {{ section.title }}
                </h5>

                <button
                  v-if="section.kind === 'keyholders'"
                  type="button"
                  class="btn-small blue-grey darken-2"
                  @click="addKeyholder"
                >
                  Add Keyholder
                </button>

                <button
                  v-if="section.kind === 'promoters'"
                  type="button"
                  class="btn-small blue-grey darken-2"
                  @click="addPromoter"
                >
                  Add Promoter
                </button>
              </div>

              <template
                v-if="section.kind === 'keyholders' || section.kind === 'promoters'"
              >
                <div class="dynamic-card-grid">
                  <div
                    v-for="card in section.cards"
                    :key="card.title"
                    class="card dynamic-entry-card"
                  >
                    <div class="card-content">
                      <span class="card-title dynamic-card-title">
                        {{ card.title }}
                      </span>

                      <div
                        v-for="item in card.fields"
                        :key="item.key"
                        class="dynamic-field"
                      >
                        <label class="dynamic-field-label">
                          {{ item.label }}
                        </label>

                        <div class="field-edit-row">
                          <input
                            v-if="item.type === 'text'"
                            v-model="form[item.key]"
                            type="text"
                            class="browser-default core-input"
                            :disabled="isFieldLocked(item)"
                          />

                          <input
                            v-else-if="item.type === 'date'"
                            v-model="form[item.key]"
                            type="date"
                            class="browser-default core-input"
                            :disabled="isFieldLocked(item)"
                          />

                          <textarea
                            v-else
                            v-model="form[item.key]"
                            class="browser-default core-textarea"
                            rows="3"
                            :disabled="isFieldLocked(item)"
                          ></textarea>

                          <button
                            v-if="showEditButton(item)"
                            type="button"
                            class="btn-small blue-grey darken-2 edit-field-btn"
                            @click="enableFieldEdit(item.key)"
                          >
                            Edit
                          </button>

                          <button
                            v-if="showLockButton(item)"
                            type="button"
                            class="btn-small grey darken-1 edit-field-btn"
                            @click="cancelFieldEdit(item.key)"
                          >
                            Lock
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else>
                <table class="striped responsive-table core-table">
                  <thead>
                    <tr>
                      <th style="width: 34%">Information</th>

                      <th></th>

                      <th
                        v-if="sectionHasUpload(section)"
                        class="right-align upload-header-cell"
                      >
                        Upload Document
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="item in section.fields" :key="item.key">
                      <td>
                        <strong>
                          {{ item.label }}
                        </strong>

                        <div v-if="item.help" class="grey-text text-darken-1 field-help">
                          {{ item.help }}
                        </div>
                      </td>

                      <td>
                        <div class="field-edit-row">
                          <input
                            v-if="item.key === 'select_venue'"
                            :value="selectedVenueValue"
                            type="text"
                            class="browser-default core-input auto-field"
                            readonly
                          />

                          <select
                            v-else-if="item.type === 'operations_user'"
                            v-model="form[item.key]"
                            class="browser-default core-input"
                            :disabled="loadingOperationsUsers || isFieldLocked(item)"
                          >
                            <option value="">
                              {{
                                loadingOperationsUsers
                                  ? "Loading operations users..."
                                  : "Select Name"
                              }}
                            </option>

                            <option
                              v-for="user in operationsUsers"
                              :key="user.uid"
                              :value="user.displayValue"
                            >
                              {{ user.label }}
                            </option>
                          </select>

                          <input
                            v-else-if="item.type === 'text'"
                            v-model="form[item.key]"
                            type="text"
                            class="browser-default core-input"
                            :disabled="isFieldLocked(item)"
                          />

                          <input
                            v-else-if="item.type === 'date'"
                            v-model="form[item.key]"
                            type="date"
                            class="browser-default core-input"
                            :disabled="isFieldLocked(item)"
                          />

                          <textarea
                            v-else
                            v-model="form[item.key]"
                            class="browser-default core-textarea"
                            rows="3"
                            :disabled="isFieldLocked(item)"
                          ></textarea>

                          <button
                            v-if="showEditButton(item)"
                            type="button"
                            class="btn-small blue-grey darken-2 edit-field-btn"
                            @click="enableFieldEdit(item.key)"
                          >
                            Edit
                          </button>

                          <button
                            v-if="showLockButton(item)"
                            type="button"
                            class="btn-small grey darken-1 edit-field-btn"
                            @click="cancelFieldEdit(item.key)"
                          >
                            Lock
                          </button>
                        </div>
                      </td>

                      <td v-if="sectionHasUpload(section)" class="upload-cell">
                        <template v-if="item.upload === true">
                          <div class="file-field-inline">
                            <input
                              type="file"
                              :disabled="uploadingKey === item.key || !activeVenueId"
                              @change="onFileSelected($event, item)"
                            />

                            <div v-if="uploadingKey === item.key" class="grey-text">
                              Uploading...
                            </div>

                            <div v-if="files[item.key]?.url" class="file-links">
                              <a
                                :href="files[item.key].url"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Open file
                              </a>

                              <button
                                type="button"
                                class="btn-flat red-text"
                                :disabled="saving"
                                @click="removeFile(item.key)"
                              >
                                Remove
                              </button>
                            </div>

                            <div
                              v-if="files[item.key]?.name"
                              class="grey-text text-darken-1 truncate"
                            >
                              {{ files[item.key].name }}
                            </div>
                          </div>
                        </template>

                        <span v-else class="grey-text"> — </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from "vue";

import { useRoute } from "vue-router";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";

import { auth, db } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

/* global M */

const route = useRoute();

const { venueId } = useVenueContext();

const loading = ref(false);

const saving = ref(false);

const error = ref("");

const success = ref("");

const uploadingKey = ref("");

const emittedVenueId = ref("");

const activeVenueName = ref("");

const loadingOperationsUsers = ref(false);

const operationsUsers = ref([]);

const loadingGmAccess = ref(false);

const canViewGmOnlyFields = ref(false);

const keyholderCount = ref(1);

const promoterCount = ref(1);

const form = reactive({});

const files = reactive({});

const savedFieldValues = reactive({});

const editingFields = reactive({});

const activeVenueId = computed(() => {
  return String(
    emittedVenueId.value || venueId.value || route.params?.venueId || ""
  ).trim();
});

const selectedVenueValue = computed(() => {
  return activeVenueName.value || activeVenueId.value;
});

function toKey(label) {
  return String(label)
    .trim()
    .replace(/[–—]/g, "-")
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .toLowerCase();
}

function field(label, options = {}) {
  return {
    label,

    key: options.key || toKey(label),

    type: options.type || "textarea",

    upload: options.upload === true,

    help: options.help || "",

    gmOnly: options.gmOnly === true,

    aliases: Array.isArray(options.aliases) ? options.aliases : [],
  };
}

function keyholderCard(index) {
  return {
    title: `Keyholder ${index}`,

    fields: [
      field("Name", {
        key: `keyholder_${index}_name`,
        type: "text",
      }),

      field("Role", {
        key: `keyholder_${index}_role`,
        type: "text",
      }),

      field("Contact Number", {
        key: `keyholder_${index}_contact_number`,
        type: "text",
      }),

      field("Key Type Held", {
        key: `keyholder_${index}_key_type_held`,
        type: "text",
      }),
    ],
  };
}

function promoterCard(index) {
  return {
    title: `Promoter ${index}`,

    fields: [
      field("Contact", {
        key: `promoter_${index}_contact`,

        aliases: [`promoter_${index}_-_contact`],
      }),

      field("Days / Events Covered", {
        key: `promoter_${index}_days_events_covered`,

        aliases: [`promoter_${index}_-_daysevents_covered`],
      }),
    ],
  };
}

const sections = computed(() => {
  const keyholderCards = Array.from(
    {
      length: keyholderCount.value,
    },

    (_, i) => keyholderCard(i + 1)
  );

  const promoterCards = Array.from(
    {
      length: promoterCount.value,
    },

    (_, i) => promoterCard(i + 1)
  );

  return [
    {
      title: "Venue & Company Details",

      fields: [
        field("Venue", {
          key: "select_venue",

          type: "text",
        }),

        field("Legal Company Name", {
          type: "text",
        }),

        field("Registered Company Number", {
          type: "text",
        }),

        field("VAT Number", {
          type: "text",
        }),

        field("Full Address"),

        field("Postcode", {
          type: "text",
        }),
      ],
    },

    {
      title: "Licensing",

      fields: [
        field("Premises Licence Number", {
          type: "text",

          upload: true,
        }),

        field("Designated Premises Supervisor (DPS)", {
          type: "text",
        }),

        field("DPS Personal Licence Number", {
          type: "text",
        }),

        field("Personal License Holder Name & Number 1"),

        field("Personal License Holder Name & Number 2"),

        field("Personal License Holder Name & Number 3"),

        field("Standard Trading Hours"),
      ],
    },

    {
      title: "Authority & Compliance Contacts",

      fields: [
        field("Local Police Station"),

        field("Police Licensing Officer - Including Contact Details"),

        field("Council Licensing Officer - Including Contact Details"),

        field("Environmental Health Officer - Including Contact Details"),

        field("Pubwatch Representative"),

        field("Fire Officer - Including Contact Details"),
      ],
    },

    {
      title: "Internal Contacts",

      fields: [
        field("Area Manager", {
          type: "operations_user",

          help: "Dropdown list from users where isOperations is true.",
        }),

        field("Director", {
          type: "operations_user",

          help: "Dropdown list from users where isOperations is true.",
        }),

        field("Director 2", {
          key: "director_2",

          type: "operations_user",

          help: "Dropdown list from users where isOperations is true.",
        }),

        field("Payroll Contact"),

        field("Maintenance Escalation Contact"),
      ],
    },

    {
      title: "Alarms, Fire & CCTV",

      fields: [
        field("Alarm Company"),

        field("Alarm Account Number", {
          type: "text",
        }),

        field("Intruder Alarm Code", {
          type: "text",
        }),

        field("Verisure Response Code", {
          type: "text",
        }),

        field("Fire Alarm Panel Location"),

        field("Fire Assembly Point"),

        field("CCTV Provider"),

        field("CCTV Login Storage Location"),
      ],
    },

    {
      title: "Utilities & Shut-Off Locations",

      fields: [
        field("Electric MPAN - serial number of meter", {
          type: "text",
        }),

        field("Electricity Meter Location"),

        field("Gas MPRN - serial number of meter", {
          type: "text",
        }),

        field("Gas Meter Location"),

        field("Water Meter Location"),

        field("Water MPRN - serial number of meter", {
          type: "text",
        }),

        field("Internet Provider"),

        field("Emergency Shut-Off Location – Gas"),

        field("Emergency Shut-Off Location – Electric"),

        field("Emergency Shut-Off Location – Water"),
      ],
    },

    {
      title: "Waste, Cleaning & Attendance",

      fields: [
        field("General Waste Contractor"),

        field("Waste Collection Days"),

        field("Glass Collection Contractor"),

        field("Grease Trap Contractor"),

        field("Cleaning Company"),

        field("Cleaner Contact Details"),

        field("Cleaners Attendance Schedule"),
      ],
    },

    {
      title: "Suppliers & Portals",

      fields: [
        field("Primary Drinks Supplier"),

        field("Online Ordering Portal Login Website Address"),

        field("Login & Password"),

        field("Wine Supplier"),

        field("Food Supplier Login & Password"),

        field("Card Machine Provider & Merchant ID"),

        field("Tills Back Office Login & Password", {
          gmOnly: true,

          help:
            "Only visible to users with General Manager job title in staff_login_codes.",
        }),
      ],
    },

    {
      title: "Statutory Documents & Safety",

      fields: [
        field("Fire Risk Assessment Location", {
          upload: true,
        }),

        field("Asbestos Register Location", {
          upload: true,
        }),

        field("Gas Safety Certificate Expiry Date", {
          type: "date",

          upload: true,
        }),

        field("EICR Expiry Date", {
          type: "date",

          upload: true,
        }),

        field("PAT Testing Expiry Date", {
          type: "date",

          upload: true,
        }),

        field("First Aid Kit Locations"),

        field("Defibrillator Location if applicable"),

        field("Accident Book Location"),
      ],
    },

    {
      title: "Keyholders",

      kind: "keyholders",

      cards: keyholderCards,

      fields: keyholderCards.flatMap((card) => card.fields),
    },

    {
      title: "Promoters",

      kind: "promoters",

      cards: promoterCards,

      fields: promoterCards.flatMap((card) => card.fields),
    },

    {
      title: "Contractors",

      fields: [
        field("Plumber"),

        field("Electrician"),

        field("Refrigeration Engineer"),

        field("Kitchen Equipment Engineer"),

        field("Pest Control Contractor"),

        field("Window Cleaner"),

        field("Cellar Services"),
      ],
    },
  ];
});

const visibleSections = computed(() => {
  return sections.value
    .map((section) => {
      const nextSection = {
        ...section,

        fields: section.fields.filter((item) => canSeeField(item)),
      };

      if (Array.isArray(section.cards)) {
        nextSection.cards = section.cards
          .map((card) => ({
            ...card,

            fields: card.fields.filter((item) => canSeeField(item)),
          }))
          .filter((card) => card.fields.length > 0);
      }

      return nextSection;
    })
    .filter((section) => {
      if (Array.isArray(section.cards)) {
        return section.cards.length > 0;
      }

      return section.fields.length > 0;
    });
});

const allFields = computed(() => sections.value.flatMap((section) => section.fields));

const visibleFields = computed(() =>
  visibleSections.value.flatMap((section) => section.fields)
);

watch(
  () => allFields.value.map((item) => item.key).join("|"),

  () => {
    allFields.value.forEach((item) => {
      if (!(item.key in form)) {
        form[item.key] = "";
      }

      if (!(item.key in savedFieldValues)) {
        savedFieldValues[item.key] = "";
      }

      if (!(item.key in editingFields)) {
        editingFields[item.key] = false;
      }
    });
  },

  {
    immediate: true,
  }
);

function addKeyholder() {
  keyholderCount.value += 1;
}

function addPromoter() {
  promoterCount.value += 1;
}

function canSeeField(item) {
  if (!item?.gmOnly) {
    return true;
  }

  return canViewGmOnlyFields.value === true;
}

function sectionHasUpload(section) {
  return section.fields.some((item) => item.upload === true);
}

function asString(value) {
  return String(value ?? "").trim();
}

function cleanString(value) {
  return String(value ?? "").trim();
}

function normaliseRole(value) {
  return cleanString(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function isGeneralManagerJobTitle(value) {
  const clean = normaliseRole(value);

  return clean === "generalmanager" || clean === "gm" || clean.includes("generalmanager");
}

function hasSavedValue(item) {
  if (!item || item.key === "select_venue") {
    return false;
  }

  return cleanString(savedFieldValues[item.key]) !== "";
}

function isFieldLocked(item) {
  if (!item || item.key === "select_venue") {
    return false;
  }

  return hasSavedValue(item) && editingFields[item.key] !== true;
}

function showEditButton(item) {
  if (!item || item.key === "select_venue") {
    return false;
  }

  return hasSavedValue(item) && editingFields[item.key] !== true;
}

function showLockButton(item) {
  if (!item || item.key === "select_venue") {
    return false;
  }

  return hasSavedValue(item) && editingFields[item.key] === true;
}

function enableFieldEdit(key) {
  editingFields[key] = true;
}

function cancelFieldEdit(key) {
  form[key] = savedFieldValues[key] || "";

  editingFields[key] = false;
}

function coreInfoDocRef() {
  const cleanVenueId = activeVenueId.value;

  if (!cleanVenueId) {
    throw new Error("No venue selected.");
  }

  return doc(db, "venues", cleanVenueId, "core_information", "state");
}

function clearMessages() {
  error.value = "";
  success.value = "";
}

function syncAutoVenueField() {
  form.select_venue = selectedVenueValue.value;
}

function resetSavedFieldState() {
  allFields.value.forEach((item) => {
    savedFieldValues[item.key] = "";

    editingFields[item.key] = false;
  });
}

function operationUserLabelFromDoc(id, data) {
  const firstName = cleanString(data.firstName || data.first_name);

  const lastName = cleanString(data.lastName || data.last_name);

  const fullFromParts = `${firstName} ${lastName}`.trim();

  const name = cleanString(
    data.displayName ||
      data.display_name ||
      data.name ||
      data.fullName ||
      data.full_name ||
      fullFromParts ||
      data.email ||
      id
  );

  const email = cleanString(data.email || data.emailLower || data.email_lower);

  return email && email !== name ? `${name} (${email})` : name;
}

function operationUserValueFromDoc(id, data) {
  const firstName = cleanString(data.firstName || data.first_name);

  const lastName = cleanString(data.lastName || data.last_name);

  const fullFromParts = `${firstName} ${lastName}`.trim();

  return cleanString(
    data.displayName ||
      data.display_name ||
      data.name ||
      data.fullName ||
      data.full_name ||
      fullFromParts ||
      data.email ||
      id
  );
}

function getSavedFieldValue(savedFields, item) {
  const directValue = cleanString(savedFields[item.key]);

  if (directValue) {
    return directValue;
  }

  for (const alias of item.aliases || []) {
    const aliasValue = cleanString(savedFields[alias]);

    if (aliasValue) {
      return aliasValue;
    }
  }

  return "";
}

function countSavedCards(savedFields, sectionType) {
  let maxIndex = 1;

  const prefixes = sectionType === "keyholders" ? ["keyholder"] : ["promoter"];

  Object.keys(savedFields || {}).forEach((key) => {
    prefixes.forEach((prefix) => {
      const match = key.match(new RegExp(`^${prefix}_(\\d+)_`));

      if (match && cleanString(savedFields[key])) {
        const index = Number(match[1]);

        if (Number.isFinite(index) && index > maxIndex) {
          maxIndex = index;
        }
      }
    });
  });

  return maxIndex;
}

async function loadOperationsUsers() {
  loadingOperationsUsers.value = true;

  try {
    const usersById = new Map();

    const queries = [
      query(collection(db, "users"), where("isOperations", "==", true)),

      query(collection(db, "users"), where("is_operations", "==", true)),

      query(collection(db, "users"), where("operations", "==", true)),
    ];

    for (const q of queries) {
      const snap = await getDocs(q);

      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};

        usersById.set(
          docSnap.id,

          {
            uid: docSnap.id,

            label: operationUserLabelFromDoc(docSnap.id, data),

            displayValue: operationUserValueFromDoc(docSnap.id, data),

            email: cleanString(data.email || data.emailLower || data.email_lower),
          }
        );
      });
    }

    operationsUsers.value = Array.from(usersById.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  } catch (err) {
    console.error("[CoreInformation] operations users load failed:", err);

    error.value = err?.message || String(err);
  } finally {
    loadingOperationsUsers.value = false;
  }
}

async function loadGmOnlyAccess() {
  loadingGmAccess.value = true;

  canViewGmOnlyFields.value = false;

  try {
    const currentEmail = cleanString(auth.currentUser?.email).toLowerCase();

    if (!currentEmail) {
      return;
    }

    const docsById = new Map();

    const queries = [
      query(collection(db, "staff_login_codes"), where("emailLower", "==", currentEmail)),

      query(
        collection(db, "staff_login_codes"),
        where("email_lower", "==", currentEmail)
      ),

      query(collection(db, "staff_login_codes"), where("email", "==", currentEmail)),
    ];

    for (const q of queries) {
      const snap = await getDocs(q);

      snap.forEach((docSnap) => {
        docsById.set(docSnap.id, docSnap.data() || {});
      });
    }

    canViewGmOnlyFields.value = Array.from(docsById.values()).some((data) => {
      return (
        isGeneralManagerJobTitle(data.jobTitle) ||
        isGeneralManagerJobTitle(data.job_title) ||
        isGeneralManagerJobTitle(data.role) ||
        isGeneralManagerJobTitle(data.position)
      );
    });
  } catch (err) {
    console.error("[CoreInformation] GM-only access lookup failed:", err);

    canViewGmOnlyFields.value = false;
  } finally {
    loadingGmAccess.value = false;
  }
}

async function loadVenueName() {
  activeVenueName.value = "";

  const cleanVenueId = activeVenueId.value;

  if (!cleanVenueId) {
    syncAutoVenueField();
    return;
  }

  try {
    const snap = await getDoc(doc(db, "venues", cleanVenueId));

    if (!snap.exists()) {
      activeVenueName.value = cleanVenueId;

      syncAutoVenueField();

      return;
    }

    const data = snap.data() || {};

    activeVenueName.value = cleanString(
      data.venue_name ||
        data.venueName ||
        data.name ||
        data.label ||
        data.title ||
        cleanVenueId
    );

    syncAutoVenueField();
  } catch (err) {
    console.warn("[CoreInformation] venue name lookup failed:", err);

    activeVenueName.value = cleanVenueId;

    syncAutoVenueField();
  }
}

async function loadCoreInformation() {
  clearMessages();

  if (!activeVenueId.value) {
    error.value = "No venue selected.";

    return;
  }

  loading.value = true;

  try {
    const snap = await getDoc(coreInfoDocRef());

    allFields.value.forEach((item) => {
      form[item.key] = "";
    });

    Object.keys(files).forEach((key) => {
      delete files[key];
    });

    resetSavedFieldState();

    syncAutoVenueField();

    if (!snap.exists()) {
      keyholderCount.value = 1;

      promoterCount.value = 1;

      success.value = "No core information has been saved for this venue yet.";

      return;
    }

    const data = snap.data() || {};

    const savedFields = data.fields || {};

    const savedFiles = data.files || {};

    keyholderCount.value = countSavedCards(savedFields, "keyholders");

    promoterCount.value = countSavedCards(savedFields, "promoters");

    await nextTick();

    allFields.value.forEach((item) => {
      if (item.key === "select_venue") {
        form[item.key] = selectedVenueValue.value;

        savedFieldValues[item.key] = selectedVenueValue.value;
      } else {
        const savedValue = getSavedFieldValue(savedFields, item);

        form[item.key] = savedValue;

        savedFieldValues[item.key] = savedValue;

        editingFields[item.key] = false;
      }
    });

    Object.keys(savedFiles).forEach((key) => {
      files[key] = savedFiles[key];
    });

    syncAutoVenueField();
  } catch (err) {
    console.error("[CoreInformation] load failed:", err);

    error.value = err?.message || String(err);
  } finally {
    loading.value = false;
  }
}

async function saveCoreInformation() {
  clearMessages();

  if (!activeVenueId.value) {
    error.value = "No venue selected.";

    return;
  }

  syncAutoVenueField();

  saving.value = true;

  try {
    const payloadFields = {};

    visibleFields.value.forEach((item) => {
      if (item.key === "select_venue") {
        payloadFields[item.key] = selectedVenueValue.value;
      } else {
        payloadFields[item.key] = cleanString(form[item.key]);
      }
    });

    await setDoc(
      coreInfoDocRef(),

      {
        venue_id: activeVenueId.value,

        venue_name: selectedVenueValue.value,

        selected_venue: selectedVenueValue.value,

        keyholder_count: keyholderCount.value,

        promoter_count: promoterCount.value,

        fields: payloadFields,

        files: {
          ...files,
        },

        updatedAt: serverTimestamp(),

        updatedByUid: auth.currentUser?.uid || null,
      },

      {
        merge: true,
      }
    );

    visibleFields.value.forEach((item) => {
      if (item.key === "select_venue") {
        savedFieldValues[item.key] = selectedVenueValue.value;
      } else {
        savedFieldValues[item.key] = cleanString(form[item.key]);
      }

      editingFields[item.key] = false;
    });

    success.value = "Core information saved.";

    M?.toast?.({
      html: "Core information saved.",
    });
  } catch (err) {
    console.error("[CoreInformation] save failed:", err);

    error.value = err?.message || String(err);

    M?.toast?.({
      html: error.value,
    });
  } finally {
    saving.value = false;
  }
}

async function onFileSelected(event, item) {
  clearMessages();

  const selectedFile = event?.target?.files?.[0] || null;

  if (event?.target) {
    event.target.value = "";
  }

  if (!selectedFile) {
    return;
  }

  if (!activeVenueId.value) {
    error.value = "No venue selected.";

    return;
  }

  uploadingKey.value = item.key;

  try {
    const storage = getStorage();

    const safeName = selectedFile.name.replace(/[^\w.\-() ]+/g, "_");

    const path = `venues/${activeVenueId.value}/core_information/${
      item.key
    }/${Date.now()}_${safeName}`;

    const fileRef = storageRef(storage, path);

    await uploadBytes(fileRef, selectedFile, {
      contentType: selectedFile.type || "application/octet-stream",

      customMetadata: {
        venueId: activeVenueId.value,

        coreInformationField: item.key,
      },
    });

    const url = await getDownloadURL(fileRef);

    files[item.key] = {
      name: selectedFile.name,

      path,

      url,

      contentType: selectedFile.type || "",

      size: selectedFile.size || 0,

      uploadedAt: new Date().toISOString(),

      uploadedByUid: auth.currentUser?.uid || null,
    };

    await saveCoreInformation();

    success.value = `${item.label} file uploaded.`;

    M?.toast?.({
      html: "File uploaded.",
    });
  } catch (err) {
    console.error("[CoreInformation] upload failed:", err);

    error.value = err?.message || String(err);

    M?.toast?.({
      html: error.value,
    });
  } finally {
    uploadingKey.value = "";
  }
}

async function removeFile(key) {
  clearMessages();

  if (files[key]) {
    delete files[key];
  }

  await saveCoreInformation();
}

function onVenueSnapshotChanged(event) {
  const nextVenueId = asString(event?.detail);

  if (!nextVenueId) {
    return;
  }

  emittedVenueId.value = nextVenueId;

  syncAutoVenueField();
}

watch(
  () => activeVenueId.value,

  async () => {
    await loadVenueName();
    await loadCoreInformation();
  },

  {
    immediate: true,
  }
);

watch(
  () => selectedVenueValue.value,

  () => {
    syncAutoVenueField();
  }
);

onMounted(() => {
  window.addEventListener("ops-venue-changed", onVenueSnapshotChanged);

  loadOperationsUsers();
  loadGmOnlyAccess();
});

onUnmounted(() => {
  window.removeEventListener("ops-venue-changed", onVenueSnapshotChanged);
});
</script>

<style scoped>
.core-information-view {
  width: 96%;
  max-width: 1500px;
}

.core-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.core-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.section-block {
  margin-top: 28px;
}

.section-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.section-block h5 {
  font-weight: 800;
  margin: 0;
}

.core-table th,
.core-table td {
  vertical-align: top;
}

.upload-header-cell {
  width: 260px;
  text-align: right !important;
  padding-right: 24px;
}

.upload-cell {
  width: 260px;
  text-align: right;
}

.upload-cell .file-field-inline {
  align-items: flex-end;
}

.field-edit-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.core-input,
.core-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
}

.core-input:disabled,
.core-textarea:disabled,
select.core-input:disabled {
  background: #f5f7f8;
  color: #455a64;
  border-color: #d5dde1;
  opacity: 1;
  cursor: not-allowed;
}

.core-textarea {
  min-height: 72px;
  resize: vertical;
}

.auto-field {
  background: #f5f7f8;
  color: #455a64;
  font-weight: 700;
}

.edit-field-btn {
  flex: 0 0 auto;
  margin-top: 0;
  white-space: nowrap;
  height: 38px;
  line-height: 38px;
}

.field-help {
  font-size: 12px;
  margin-top: 4px;
}

.file-field-inline {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.file-links {
  display: flex;
  gap: 8px;
  align-items: center;
}

.truncate {
  max-width: 240px;
}

.dynamic-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 18px;
}

.dynamic-entry-card {
  border-radius: 10px;
  overflow: hidden;
  margin: 0;
}

.dynamic-card-title {
  font-weight: 800 !important;
  margin-bottom: 16px !important;
  display: block;
}

.dynamic-field {
  margin-bottom: 16px;
}

.dynamic-field:last-child {
  margin-bottom: 0;
}

.dynamic-field-label {
  display: block;
  color: #263238;
  font-weight: 700;
  margin-bottom: 6px;
}

@media (max-width: 700px) {
  .core-actions {
    width: 100%;
  }

  .core-actions .btn {
    width: 100%;
  }

  .field-edit-row {
    flex-direction: column;
  }

  .edit-field-btn {
    width: 100%;
  }

  .section-heading-row .btn-small {
    width: 100%;
  }

  .dynamic-card-grid {
    grid-template-columns: 1fr;
  }

  .upload-cell,
  .upload-header-cell {
    width: auto;
    text-align: left !important;
  }

  .upload-cell .file-field-inline {
    align-items: flex-start;
  }
}
</style>
